import { useQuery, useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import axiosInstance from '../../../shared/api/axiosInstance';
import type { UnreadSummaryResponse } from '../../../generated/api/models/unreadSummaryResponse';
import type {
  Conversation,
  Message,
  MessageEdit,
  CreateConversationRequest,
  CreateMessageRequest,
  UpdateMessageRequest } from '../types';

interface OptimisticSender {
  id: string;
  display_name: string;
  email: string;
  avatar_url?: string;
}

const API_BASE = '/api/v1/messaging';

// Query keys
export const messagingKeys = {
  all: ['messaging'] as const,
  conversations: () => [...messagingKeys.all, 'conversations'] as const,
  conversation: (id: string) => [...messagingKeys.conversations(), id] as const,
  messages: (conversationId: string) => [...messagingKeys.all, 'messages', conversationId] as const,
  thread: (messageId: string) => [...messagingKeys.all, 'thread', messageId] as const,
  members: (conversationId: string) => [...messagingKeys.all, 'members', conversationId] as const,
  unreadSummary: () => [...messagingKeys.all, 'unread-summary'] as const,
  messageEdits: (messageId: string) => [...messagingKeys.all, 'edits', messageId] as const };

// Conversations
export function useConversations() {
  return useQuery({
    queryKey: messagingKeys.conversations(),
    queryFn: async (): Promise<Conversation[]> => {
      const { data } = await axiosInstance.get<Conversation[]>(`${API_BASE}/conversations`);
      return data;
    },
    refetchInterval: 30000, // Poll every 30 seconds for new conversations
  });
}

export function useConversation(id: string | undefined) {
  return useQuery({
    queryKey: messagingKeys.conversation(id!),
    queryFn: async (): Promise<Conversation> => {
      const { data } = await axiosInstance.get<Conversation>(`${API_BASE}/conversations/${id}`);
      return data;
    },
    enabled: !!id });
}

export function useCreateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateConversationRequest): Promise<Conversation> => {
      const { data } = await axiosInstance.post<Conversation>(`${API_BASE}/conversations`, request);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    } });
}

export function useUpdateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<Conversation>): Promise<Conversation> => {
      const { data } = await axiosInstance.put<Conversation>(`${API_BASE}/conversations/${id}`, updates);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
      queryClient.setQueryData(messagingKeys.conversation(data.id), data);
    } });
}

// Messages
const MESSAGES_PAGE_SIZE = 50;

export function useMessages(conversationId: string | undefined) {
  return useInfiniteQuery({
    queryKey: messagingKeys.messages(conversationId!),
    queryFn: async ({ pageParam }: { pageParam: string | undefined }): Promise<Message[]> => {
      const params = new URLSearchParams();
      params.append('limit', MESSAGES_PAGE_SIZE.toString());
      if (pageParam) params.append('before', pageParam);
      const { data } = await axiosInstance.get<Message[]>(
        `${API_BASE}/conversations/${conversationId}/messages?${params}`
      );
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (firstPage) => {
      // "Next page" loads older messages — cursor is the oldest message's created_at
      if (firstPage.length < MESSAGES_PAGE_SIZE) return undefined; // No more pages
      return firstPage[0]?.created_at;
    },
    enabled: !!conversationId,
    refetchInterval: 5000,
    // Only refetch the latest page (first page) on interval, not all pages
    refetchOnWindowFocus: false,
  });
}

export function useThreadReplies(threadId: string | undefined) {
  return useQuery({
    queryKey: messagingKeys.thread(threadId!),
    queryFn: async (): Promise<Message[]> => {
      const { data } = await axiosInstance.get<Message[]>(
        `${API_BASE}/messages/${threadId}/replies`
      );
      return data;
    },
    enabled: !!threadId,
    refetchInterval: 5000 });
}

export function useSendMessage(conversationId: string, sender?: OptimisticSender) {
  const queryClient = useQueryClient();
  const messagesKey = messagingKeys.messages(conversationId);
  return useMutation({
    mutationFn: async (request: CreateMessageRequest): Promise<Message> => {
      const { data } = await axiosInstance.post<Message>(
        `${API_BASE}/conversations/${conversationId}/messages`,
        request
      );
      return data;
    },
    onMutate: async (request): Promise<{ tempId: string } | undefined> => {
      if (!sender) return undefined;
      await queryClient.cancelQueries({ queryKey: messagesKey });
      const tempId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const nowIso = new Date().toISOString();
      const optimistic: Message = {
        id: tempId,
        tenant_id: '',
        conversation_id: conversationId,
        user_id: sender.id,
        thread_id: request.thread_id ?? null,
        content: request.content,
        message_type: 'text',
        is_edited: false,
        edited_at: null,
        reply_count: 0,
        scheduled_at: null,
        metadata: { __optimistic: true },
        created_at: nowIso,
        deleted_at: null,
        user: {
          id: sender.id,
          display_name: sender.display_name,
          email: sender.email,
          avatar_url: sender.avatar_url,
        },
        reactions: [],
        attachments: [],
        parent_message: null,
      };
      queryClient.setQueryData<InfiniteData<Message[]>>(messagesKey, (prev) => {
        if (!prev || prev.pages.length === 0) {
          return { pages: [[optimistic]], pageParams: [undefined] };
        }
        const firstPage = [...prev.pages[0], optimistic];
        return { ...prev, pages: [firstPage, ...prev.pages.slice(1)] };
      });
      return { tempId };
    },
    onError: (_err, _vars, context) => {
      if (!context?.tempId) return;
      const tempId = context.tempId;
      queryClient.setQueryData<InfiniteData<Message[]>>(messagesKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pages: prev.pages.map((page) => page.filter((m) => m.id !== tempId)),
        };
      });
    },
    onSuccess: (serverMessage, _vars, context) => {
      if (context?.tempId) {
        const tempId = context.tempId;
        queryClient.setQueryData<InfiniteData<Message[]>>(messagesKey, (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            pages: prev.pages.map((page) =>
              page.map((m) => (m.id === tempId ? serverMessage : m))
            ),
          };
        });
      }
      queryClient.invalidateQueries({ queryKey: messagesKey });
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    } });
}

export function useUpdateMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ messageId, ...request }: { messageId: string } & UpdateMessageRequest): Promise<Message> => {
      const { data } = await axiosInstance.put<Message>(`${API_BASE}/messages/${messageId}`, request);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.messages(data.conversation_id) });
    } });
}

export function useDeleteMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (messageId: string): Promise<void> => {
      await axiosInstance.delete(`${API_BASE}/messages/${messageId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.all });
    } });
}

// Reactions
export function useAddReaction(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ messageId, emoji }: { messageId: string; emoji: string }): Promise<void> => {
      await axiosInstance.post(`${API_BASE}/messages/${messageId}/reactions`, { emoji });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.messages(conversationId) });
    } });
}

export function useRemoveReaction(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ messageId, emoji }: { messageId: string; emoji: string }): Promise<void> => {
      await axiosInstance.delete(`${API_BASE}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.messages(conversationId) });
    } });
}

// Members
export function useConversationMembers(conversationId: string | undefined) {
  return useQuery({
    queryKey: messagingKeys.members(conversationId!),
    queryFn: async () => {
      const { data } = await axiosInstance.get(`${API_BASE}/conversations/${conversationId}/members`);
      return data;
    },
    enabled: !!conversationId });
}

export function useAddMember(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string): Promise<void> => {
      await axiosInstance.post(`${API_BASE}/conversations/${conversationId}/members`, { user_id: userId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.members(conversationId) });
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversation(conversationId) });
    } });
}

export function useRemoveMember(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string): Promise<void> => {
      await axiosInstance.delete(`${API_BASE}/conversations/${conversationId}/members/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.members(conversationId) });
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversation(conversationId) });
    } });
}

// Mark as read
export function useMarkAsRead(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await axiosInstance.post(`${API_BASE}/conversations/${conversationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
      queryClient.invalidateQueries({ queryKey: messagingKeys.unreadSummary() });
    } });
}

// Edit history
export function useMessageEditHistory(messageId: string | undefined) {
  return useQuery({
    queryKey: messagingKeys.messageEdits(messageId!),
    queryFn: async (): Promise<MessageEdit[]> => {
      const { data } = await axiosInstance.get<MessageEdit[]>(
        `${API_BASE}/messages/${messageId}/edits`
      );
      return data;
    },
    enabled: !!messageId,
  });
}

// Search
export function useSearchMessages(query: string, conversationId?: string) {
  return useQuery({
    queryKey: [...messagingKeys.all, 'search', query, conversationId],
    queryFn: async (): Promise<Message[]> => {
      const params = new URLSearchParams({ q: query });
      if (conversationId) params.append('conversation_id', conversationId);
      const { data } = await axiosInstance.get<Message[]>(`${API_BASE}/search?${params}`);
      return data;
    },
    enabled: query.length >= 2 });
}

// Typing indicator — fire-and-forget, no cache work.
// Server throttles to 1 event per 2s per (user, conversation).
export function useSendTyping() {
  return useMutation({
    mutationFn: async (conversationId: string): Promise<void> => {
      await axiosInstance.post(`${API_BASE}/conversations/${conversationId}/typing`);
    },
  });
}

// Unread summary (AppBar inbox popover: total + top-N recent unread conversations)
export function useMessagingUnreadSummary(limit = 5) {
  return useQuery({
    queryKey: [...messagingKeys.unreadSummary(), limit],
    queryFn: async (): Promise<UnreadSummaryResponse> => {
      const { data } = await axiosInstance.get<UnreadSummaryResponse>(
        `${API_BASE}/unread-summary`,
        { params: { limit } },
      );
      return data;
    },
    refetchInterval: 30000, // Poll every 30 seconds
  });
}
